import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth.js";
import { LoginPage } from "./pages/Login.js";
import { DevicesPage } from "./pages/Devices.js";
import { SessionsPage } from "./pages/Sessions.js";
import { ViewerPage } from "./pages/Viewer.js";
import { ConnectPage } from "./pages/Connect.js";
import { ConnectByCodePage } from "./pages/ConnectByCode.js";
import { AdminPage } from "./pages/Admin.js";
import { CostsPage } from "./pages/Costs.js";
import { APP_DOWNLOAD_URL, APP_DOWNLOAD_NAME } from "./lib/app.js";
import { ConfirmProvider } from "./components/ConfirmDialog.js";
import type { ReactNode } from "react";

function Header() {
  const { me, logout } = useAuth();
  return (
    <header className="px-6 py-4 backdrop-blur-md bg-gradient-to-r from-slate-900/50 to-slate-800/30 border-b border-white/5 flex items-center gap-6">
      <Link to="/" className="flex items-center gap-2 font-bold text-lg tracking-tight text-white hover:opacity-80 transition">
        <img src="/logo.png" alt="SPIDER Viewer" className="w-7 h-7" />
        SPIDER® Viewer
      </Link>
      {me && (
        <>
          <nav className="flex gap-1 text-sm">
            <Link
              to="/connect"
              className="px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition"
            >
              Connect
            </Link>
            <Link
              to="/devices"
              className="px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition"
            >
              Devices
            </Link>
            <Link
              to="/sessions"
              className="px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition"
            >
              Sessions
            </Link>
            <a
              href={APP_DOWNLOAD_URL}
              download={APP_DOWNLOAD_NAME}
              className="px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
              </svg>
              Download
            </a>
            {me.isAdmin && (
              <>
                <Link
                  to="/admin"
                  className="px-3 py-2 rounded-lg text-purple-300 hover:text-white hover:bg-purple-500/15 transition"
                >
                  Admin
                </Link>
                <Link
                  to="/costs"
                  className="px-3 py-2 rounded-lg text-purple-300 hover:text-white hover:bg-purple-500/15 transition"
                >
                  Costs
                </Link>
              </>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-white/70">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12a3 3 0 100-6 3 3 0 000 6z" />
                <path d="M12 14c-6 0-8 3-8 3v4h16v-4s-2-3-8-3z" />
              </svg>
              <span className="font-medium">{me.display_name}</span>
            </div>
            <button
              type="button"
              onClick={logout}
              className="px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-red-500/10 transition text-sm"
            >
              Sign out
            </button>
          </div>
        </>
      )}
    </header>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-6 text-white/40">Loading…</div>;
  // Remember where the user was headed (e.g. a /connect?id=<code> deep link from
  // the desktop app) so Login can send them back there after signing in, instead
  // of dropping the query string and dumping them on /devices.
  if (!me) return <Navigate to="/login" replace state={{ from: location }} />;
  return <>{children}</>;
}

function Shell() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dev-connect" element={<ConnectPage />} />
      <Route
        path="/viewer/:sessionId"
        element={
          <RequireAuth>
            <ViewerPage />
          </RequireAuth>
        }
      />
      <Route
        path="*"
        element={
          <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1 p-6">
              <Routes>
                <Route
                  path="/"
                  element={
                    <RequireAuth>
                      <Navigate to="/connect" replace />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/connect"
                  element={
                    <RequireAuth>
                      <ConnectByCodePage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/devices"
                  element={
                    <RequireAuth>
                      <DevicesPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/sessions"
                  element={
                    <RequireAuth>
                      <SessionsPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <RequireAuth>
                      <AdminPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/costs"
                  element={
                    <RequireAuth>
                      <CostsPage />
                    </RequireAuth>
                  }
                />
              </Routes>
            </main>
          </div>
        }
      />
    </Routes>
  );
}

export function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <Shell />
      </ConfirmProvider>
    </AuthProvider>
  );
}
